import { supabase } from "@core/lib/supabase";
import { logger } from "../lib/logger";
import { getSupportProduct } from "./supportProduct";

export interface FunnelMetrics {
  anonymous: { current: number; previous: number; deltaPct: number };
  signedUp: { total: number; new30d: number };
  active7d: { count: number; pctOfSignedUp: number };
  paying: { count: number; new30d: number; churn30d: number; mrrEur: number };
  conversion: {
    anonToSignup: number;
    signupToActive: number;
    activeToPaying: number;
  };
  generatedAt: string;
}

export interface TimeSeriesPoint {
  date: string;
  anonymous: number;
  signedUp: number;
  active: number;
  paying: number;
}

export type PulseEventType =
  | "new_paying"
  | "churn"
  | "engaged_anon"
  | "first_poll";

export interface CohortRetentionPoint {
  q_offset: number;
  retained: number;
}

export interface QuarterlyCohort {
  cohort_quarter: string;
  size: number;
  retention: CohortRetentionPoint[];
}

export interface CohortMatrix {
  max_offset: number;
  cohorts: QuarterlyCohort[];
}

export interface PulseUserRow {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  plan_type: string;
  lemonsqueezy_status: string | null;
  is_test: boolean;
  polls_count: number;
  last_activity_at: string | null;
  /** Usage réel du chat/éditeur IA sur les 30 derniers jours (cf. UsageAnalyticsTracker). */
  ai_chat_messages_30d: number;
  ai_generations_30d: number;
  manual_edits_30d: number;
  /**
   * Vrai ledger de quota IA (user_ai_credits, scopé au produit courant) —
   * contrairement aux compteurs *_30d ci-dessus, dit si le compte est
   * réellement bloqué (quotas anonyme/gratuit à vie, pas mensuels).
   * `null` = aucune ligne de crédits encore créée pour ce user/produit.
   */
  ai_credits_remaining: number | null;
  ai_credits_used: number | null;
}

export interface PulseEvent {
  type: PulseEventType;
  at: string;
  label: string;
  meta: Record<string, unknown>;
}

/** Une ligne de la frise support (`admin_get_user_timeline`). */
export type AdminTimelineSource =
  | "auth"
  | "ai"
  | "ai_event"
  | "poll"
  | "invitation"
  | "vote"
  | "vote_received"
  | "quota"
  | "email"
  | "support";

export interface AdminTimelineEntry {
  ts: string;
  source: AdminTimelineSource | string;
  kind: string;
  poll_id: string | null;
  summary: string;
  detail: Record<string, unknown> | null;
}

interface FunnelRpcRow {
  days: number;
  anonymous: number;
  anonymous_previous: number;
  signed_up_total: number;
  signed_up_new: number;
  active: number;
  paying_current: number;
  paying_new: number;
  paying_churn: number;
  mrr_eur: number;
  generated_at: string;
}

const pct = (num: number, denom: number): number =>
  denom > 0 ? Math.round((num / denom) * 1000) / 10 : 0;

export class PulseMetricsService {
  private static instance: PulseMetricsService;

  public static getInstance(): PulseMetricsService {
    if (!PulseMetricsService.instance) {
      PulseMetricsService.instance = new PulseMetricsService();
    }
    return PulseMetricsService.instance;
  }

  async getFunnel(days: number = 30): Promise<FunnelMetrics> {
    const { data, error } = await supabase.rpc("get_pulse_funnel", { days });
    if (error) {
      logger.error("[PulseMetricsService] get_pulse_funnel failed", error);
      throw error;
    }
    const row = data as FunnelRpcRow;

    const anonDelta =
      row.anonymous_previous > 0
        ? Math.round(
            ((row.anonymous - row.anonymous_previous) /
              row.anonymous_previous) *
              1000,
          ) / 10
        : 0;

    return {
      anonymous: {
        current: row.anonymous,
        previous: row.anonymous_previous,
        deltaPct: anonDelta,
      },
      signedUp: {
        total: row.signed_up_total,
        new30d: row.signed_up_new,
      },
      active7d: {
        count: row.active,
        pctOfSignedUp: pct(row.active, row.signed_up_total),
      },
      paying: {
        count: row.paying_current,
        new30d: row.paying_new,
        churn30d: row.paying_churn,
        mrrEur: Number(row.mrr_eur) || 0,
      },
      conversion: {
        anonToSignup: pct(row.signed_up_new, row.anonymous),
        signupToActive: pct(row.active, row.signed_up_total),
        activeToPaying: pct(row.paying_current, row.active),
      },
      generatedAt: row.generated_at,
    };
  }

  async getTimeSeries(days: number = 90): Promise<TimeSeriesPoint[]> {
    const { data, error } = await supabase.rpc("get_pulse_timeseries", {
      days,
    });
    if (error) {
      logger.error("[PulseMetricsService] get_pulse_timeseries failed", error);
      throw error;
    }
    return (data ?? []) as TimeSeriesPoint[];
  }

  async getUsers(
    search: string | null = null,
    limit = 50,
    offset = 0,
    hideTest = false,
    product: string = getSupportProduct(),
  ): Promise<{ total: number; rows: PulseUserRow[] }> {
    const { data, error } = await supabase.rpc("get_pulse_users", {
      search,
      limit_count: limit,
      offset_count: offset,
      hide_test: hideTest,
      product_filter: product,
    });
    if (error) {
      logger.error("[PulseMetricsService] get_pulse_users failed", error);
      throw error;
    }
    return (data ?? { total: 0, rows: [] }) as {
      total: number;
      rows: PulseUserRow[];
    };
  }

  async getQuarterlyCohorts(maxOffset = 4): Promise<CohortMatrix> {
    const { data, error } = await supabase.rpc("get_pulse_cohort_quarterly", {
      max_offset: maxOffset,
    });
    if (error) {
      logger.error(
        "[PulseMetricsService] get_pulse_cohort_quarterly failed",
        error,
      );
      throw error;
    }
    return (data ?? { max_offset: maxOffset, cohorts: [] }) as CohortMatrix;
  }

  async setUserTestFlag(userId: string, isTest: boolean): Promise<void> {
    const { error } = await supabase.rpc("set_user_test_flag", {
      target_user_id: userId,
      is_test: isTest,
    });
    if (error) {
      logger.error("[PulseMetricsService] set_user_test_flag failed", error);
      throw error;
    }
  }

  async setFingerprintTestFlag(
    fingerprint: string,
    isTest: boolean,
  ): Promise<void> {
    const { error } = await supabase.rpc("set_fingerprint_test_flag", {
      target_fingerprint: fingerprint,
      is_test: isTest,
    });
    if (error) {
      logger.error(
        "[PulseMetricsService] set_fingerprint_test_flag failed",
        error,
      );
      throw error;
    }
  }

  /**
   * Frise support d'un utilisateur, session invitée comprise (C5).
   *
   * Réservée aux administrateurs (la RPC lève 42501 sinon) et journalisée
   * côté serveur : chaque appel écrit une ligne dans `private.admin_access_log`
   * — n'appeler que sur action explicite, jamais en tâche de fond.
   */
  async getUserTimeline(
    userId: string,
    sinceDays = 30,
    limit = 500,
  ): Promise<AdminTimelineEntry[]> {
    const since = new Date(
      Date.now() - sinceDays * 24 * 60 * 60 * 1000,
    ).toISOString();
    const { data, error } = await supabase.rpc("admin_get_user_timeline", {
      p_user_id: userId,
      p_since: since,
      p_limit: limit,
    });
    if (error) {
      logger.error(
        "[PulseMetricsService] admin_get_user_timeline failed",
        error,
      );
      throw error;
    }
    return (data ?? []) as AdminTimelineEntry[];
  }

  async getRecentEvents(
    limit = 20,
    includeTests = false,
  ): Promise<PulseEvent[]> {
    const { data, error } = await supabase.rpc("get_pulse_events", {
      limit_count: limit,
      include_tests: includeTests,
    });
    if (error) {
      logger.error("[PulseMetricsService] get_pulse_events failed", error);
      throw error;
    }
    return (data ?? []) as PulseEvent[];
  }
}

export const pulseMetricsService = PulseMetricsService.getInstance();
